<template>
  <div>
    <!-- Clone button (visible when rows selected) -->
    <div v-if="selectedRows.length > 0" class="selection-toolbar">
      <span class="selection-count">
        {{ selectedRows.length }} {{ t('selecionados') }}
      </span>
      <app-button
        :label="t('Clonar para Franquia')"
        icon="pi pi-copy"
        severity="info"
        outlined
        size="small"
        data-testid="so-service-priorities-clone-btn"
        @click="openCloneModal"
      />
    </div>

    <data-table
      ref="datatableRef"
      v-model:selection="selectedRows"
      :config="datatableConfig"
      :endpoint="endpoint"
      :autoLoad="true"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @inline-save="handleInlineSave"
      @inline-delete="handleInlineDelete"
    />

    <!-- Clone Modal -->
    <CloneToFranchiseModal
      ref="cloneModalRef"
      :franchises="franchiseOptions"
      :excludeFranchiseIds="selectedFranchiseIds"
      @clone="handleClone"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import { useSOServicePriorities } from '../../composables/useSOServicePriorities';
import { useSOCatalog } from '../../composables/useSOCatalog';
import DataTable from '../../grid/DataTable.vue';
import CloneToFranchiseModal from './CloneToFranchiseModal.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CloneModalInstance {
  open: (ids: Id[]) => void;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
}

interface ServicePriorityRow {
  so_service_priority_id?: Id;
  so_service_priority_franchise_id?: Id;
  so_service_priority_service_id?: Id;
  so_service_priority_service_name?: string;
  so_service_priority_priority_id?: Id;
  so_service_priority_priority_name?: string;
  so_service_priority_price?: number;
  so_service_priority_deadline_days?: number;
  so_service_priority_order?: number;
  so_service_color?: string;
  so_priority_color?: string;
  franquia?: string;
  [key: string]: unknown;
}

interface FranchiseOption {
  ID: Id;
  Name: string;
}

interface KVItem {
  value: string | number;
  label: string;
}

interface InlineSavePayload {
  data: ServicePriorityRow;
  isNew: boolean;
}

interface InlineDeletePayload {
  data: ServicePriorityRow;
}

interface ClonePayload {
  ids: Id[];
  targetFranchiseId: Id;
}

const { t } = useI18n();
const { initialized, getSelectedFranchisesSelectArray } = useFiltro();
const { save, remove, clone: clonePriorities } = useSOServicePriorities();
const { fetchKV: fetchServicesKV } = useSOCatalog('services');
const { fetchKV: fetchPrioritiesKV } = useSOCatalog('priorities');

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);
const cloneModalRef = ref<CloneModalInstance | null>(null);
const selectedRows = ref<ServicePriorityRow[]>([]);
const serviceOptions = ref<FranchiseOption[]>([]);
const priorityOptions = ref<FranchiseOption[]>([]);
const franchiseOptions = ref<FranchiseOption[]>([]);

// Computed: unique franchise IDs from selected rows
const selectedFranchiseIds = computed<number[]>(() => {
  const ids = selectedRows.value
    .map(r => Number(r.so_service_priority_franchise_id))
    .filter(Boolean);
  return [...new Set(ids)];
});

// Load dropdown options
const loadOptions = async (): Promise<void> => {
  try {
    const [servicesKV, prioritiesKV] = await Promise.all([
      fetchServicesKV() as Promise<KVItem[]>,
      fetchPrioritiesKV() as Promise<KVItem[]>
    ]);

    serviceOptions.value = servicesKV.map((item: KVItem) => ({
      ID: parseInt(String(item.value), 10),
      Name: item.label
    }));

    priorityOptions.value = prioritiesKV.map((item: KVItem) => ({
      ID: parseInt(String(item.value), 10),
      Name: item.label
    }));

    const franchises = await getSelectedFranchisesSelectArray();
    franchiseOptions.value = (franchises as FranchiseOption[]) || [];
  } catch (err) {
    logError('SOServicePrioritiesTable', 'loadOptions', 'Error', err);
  }
};

// Computed properties
const endpoint = computed<string>(() => '/index.php/so/service_priorities/lista');

const tableColumns = computed(() => [
  {
    data: 'so_service_priority_id',
    title: t('ID'),
    width: 60,
    sortable: true,
    editable: false
  },
  {
    data: 'so_service_priority_franchise_id',
    title: t('Franquia'),
    width: 150,
    sortable: true,
    filterType: 'text',
    editType: 'select',
    editOptions: franchiseOptions.value,
    editDisabledOnEdit: true,
    cellRenderer: (params: CellParams): string => {
      const row = (params.data || {}) as ServicePriorityRow;
      const id = row.so_service_priority_franchise_id;
      const franchise = franchiseOptions.value.find(f => String(f.ID) === String(id));
      return franchise ? franchise.Name : (row.franquia || (id !== undefined ? String(id) : '') || '');
    }
  },
  {
    data: 'so_service_priority_service_id',
    title: t('Serviço'),
    width: 200,
    sortable: true,
    filterType: 'text',
    editType: 'select',
    editOptions: serviceOptions.value,
    cellRenderer: (params: CellParams): string => {
      const row = (params.data || {}) as ServicePriorityRow;
      return row.so_service_priority_service_name || '';
    }
  },
  {
    data: 'so_service_priority_priority_id',
    title: t('Prioridade'),
    width: 150,
    sortable: true,
    filterType: 'text',
    editType: 'select',
    editOptions: priorityOptions.value,
    cellRenderer: (params: CellParams): string => {
      const row = (params.data || {}) as ServicePriorityRow;
      return row.so_service_priority_priority_name || '';
    }
  },
  {
    data: 'so_service_priority_price',
    title: t('Preço'),
    width: 130,
    sortable: true,
    renderType: 'currency',
    editType: 'currency'
  },
  {
    data: 'so_service_priority_deadline_days',
    title: t('Prazo (dias)'),
    width: 100,
    sortable: true,
    editType: 'number',
    editMin: 0
  },
  {
    data: 'so_service_priority_order',
    title: t('Ordem'),
    width: 90,
    sortable: true,
    renderType: 'integer',
    editType: 'number',
    editMin: 0
  },
  {
    data: '',
    title: t('Cores'),
    width: 120,
    sortable: false,
    editable: false,
    cellRenderer: (params: CellParams): string => {
      const row = (params.data || {}) as ServicePriorityRow;
      const serviceColor = row.so_service_color ? String(row.so_service_color).replace(/[^#0-9a-fA-F]/g, '') : '';
      const priorityColor = row.so_priority_color ? String(row.so_priority_color).replace(/[^#0-9a-fA-F]/g, '') : '';
      if (!serviceColor && !priorityColor) return '';
      const bg = serviceColor || '#ccc';
      const border = priorityColor || 'gray';
      const title = `bg: ${serviceColor || '#ccc'} / border: ${priorityColor || 'gray'}`;
      return `<span data-testid="so-service-priorities-color-chip" style="display:inline-block;width:1.2rem;height:1.2rem;border-radius:4px;background:${bg};border:3px solid ${border};" title="${title}"></span>`;
    }
  }
]);

const datatableConfig = computed(() => ({
  tableId: 'so-service-priorities',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.so_service_priority_id;
    return id == null ? '' : `so-service-priorities-row-${id}`;
  },
  filename: `so_service_priorities_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 25,
  pagination: true,
  sortable: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  inlineEditing: true,
  inlineAddLabel: t('Adicionar'),
  newRowDefaults: {
    so_service_priority_price: 0,
    so_service_priority_deadline_days: 1,
    so_service_priority_order: 0,
    so_service_priority_active: 1
  },
  checkbox: {
    enabled: true,
    position: 'first'
  }
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV')
}));

// Methods
const loadData = (): void => {
  selectedRows.value = [];
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: ServicePriorityRow[]): void => {
  log('SOServicePrioritiesTable', 'handleDataLoaded', 'Loaded', data.length, 'rows');
};

const handleDataError = (error: { message?: string } | Error): void => {
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar matriz serviço-prioridade') + ': ' + message);
};

const handleInlineSave = async (event: InlineSavePayload): Promise<void> => {
  const { data, isNew } = event;

  try {
    const price = Math.round((data.so_service_priority_price || 0) * 100);
    const deadlineDays = parseInt(String(data.so_service_priority_deadline_days), 10) || 0;
    const optionalFields: { so_service_priority_order?: number } = {};
    if (data.so_service_priority_order != null && String(data.so_service_priority_order) !== '') {
      optionalFields.so_service_priority_order = parseInt(String(data.so_service_priority_order), 10) || 0;
    }

    if (isNew) {
      await save({
        so_service_priority_service_id: Number(data.so_service_priority_service_id),
        so_service_priority_priority_id: Number(data.so_service_priority_priority_id),
        so_service_priority_price: price,
        so_service_priority_deadline_days: deadlineDays,
        so_service_priority_franchise_id: data.so_service_priority_franchise_id !== undefined ? Number(data.so_service_priority_franchise_id) : undefined,
        ...optionalFields
      });
    } else {
      await save({
        so_service_priority_service_id: Number(data.so_service_priority_service_id),
        so_service_priority_priority_id: Number(data.so_service_priority_priority_id),
        so_service_priority_price: price,
        so_service_priority_deadline_days: deadlineDays,
        so_service_priority_id: Number(data.so_service_priority_id),
        ...optionalFields
      });
    }
    eventBus.emit('globalMessage.success', isNew
      ? t('Registro criado com sucesso')
      : t('Registro atualizado com sucesso')
    );
    loadData();
  } catch (err: unknown) {
    logError('SOServicePrioritiesTable', 'handleInlineSave', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar') + ': ' + message);
    loadData();
  }
};

const handleInlineDelete = async (event: InlineDeletePayload): Promise<void> => {
  const { data } = event;

  try {
    await remove({ so_service_priority_id: Number(data.so_service_priority_id) });
    eventBus.emit('globalMessage.success', t('Registro removido com sucesso'));
    loadData();
  } catch (err: unknown) {
    logError('SOServicePrioritiesTable', 'handleInlineDelete', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao remover') + ': ' + message);
  }
};

// Clone
const openCloneModal = (): void => {
  const ids = selectedRows.value
    .map(r => r.so_service_priority_id)
    .filter((id): id is Id => id !== undefined);
  if (cloneModalRef.value) {
    cloneModalRef.value.open(ids);
  }
};

const handleClone = async ({ ids, targetFranchiseId }: ClonePayload): Promise<void> => {
  log('SOServicePrioritiesTable', 'handleClone', 'Cloning', { ids, targetFranchiseId });

  try {
    const result = await clonePriorities({
      ids: ids.map((id) => Number(id)),
      target_franchise_id: Number(targetFranchiseId)
    }) as { success: boolean; dados?: { cloned?: number }; error?: string };

    if (result.success) {
      eventBus.emit('globalMessage.success',
        t('Clonados') + ` ${result.dados?.cloned ?? 0} ` + t('registros com sucesso')
      );
      selectedRows.value = [];
      loadData();
    } else {
      throw new Error(result.error);
    }
  } catch (err: unknown) {
    logError('SOServicePrioritiesTable', 'handleClone', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao clonar') + ': ' + message);
  }
};

const handleFilterUpdated = (): void => {
  loadData();
  loadOptions();
};

// Lifecycle
onMounted(async () => {
  await loadOptions();

  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('so-service-priorities.saved', loadData);
  eventBus.on('so-service-priorities.deleted', loadData);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('so-service-priorities.saved', loadData);
  eventBus.off('so-service-priorities.deleted', loadData);
});

defineExpose({ loadData });
</script>

<style scoped>
.selection-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: var(--p-highlight-background);
  border-radius: var(--p-border-radius);
}

.selection-count {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-highlight-color);
}
</style>
